import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { finalize, of, switchMap, take, tap } from 'rxjs';

import { TabSyncService } from '@auth/service/tab-sync.service';
import { AuthSessionService } from '@common/service/auth-session.service';
import { DomainService } from '@common/service/domain.service';
import { FeatureFlagService } from '@common/service/feature-flag.service';
import { MediaGrantService } from '@common/service/media-grant.service';
import { SystemNotificationService } from '@common/service/system-notification.service';
import { UserService } from '@common/service/user.service';
import { ModuleStore } from '@modules/store/module.store';

@Injectable({
  providedIn: 'root',
})
export class EntityService {
  constructor(
    private httpClient: HttpClient,
    private userService: UserService,
    private moduleStore: ModuleStore,
    private tabSyncService: TabSyncService,
    private domainService: DomainService,
    private systemNotification: SystemNotificationService,
    private featureFlag: FeatureFlagService,
    private mediaGrant: MediaGrantService,
    private authSession: AuthSessionService,
  ) {}

  switchEntity(entityId: string) {
    this.moduleStore.reset();

    this.systemNotification.showSpinner('Switching entity...');

    // AK-476 (WP-F3): in cookie mode the server rotates the session and writes new
    // CloudFront cookies for the new entity in this response, so the switch needs
    // `withCredentials`. All cookie-mode work is gated on the master toggle; with
    // it OFF (the default) `withCredentials` is false — byte-for-byte today's call.
    const cookieMode = this.featureFlag.cookieSessionMigrationEnabled;

    const params = new HttpParams().set('entity_id', entityId);
    return this.httpClient.get('/api/entity/switch', { params, withCredentials: cookieMode }).pipe(
      take(1),
      tap(() => {
        // Broadcast entity switch to all other tabs
        this.tabSyncService.broadcastEntitySwitch(entityId);

        // Drop client-side media grant state: the new entity's signed cookies are
        // already set by the switch response, so the next render re-grants fresh.
        if (cookieMode) {
          this.mediaGrant.reset();
        }
      }),
      // Refresh the cookie session/entity context after the rotation.
      switchMap(() => (cookieMode ? this.authSession.load() : of(null))),
      switchMap(() => this.domainService.fetchDomainInfo()),
      switchMap(() => this.userService.getUserInfo(true)),
      finalize(() => {
        this.systemNotification.hide();
      }),
    );
  }
}
